// @ts-nocheck
import React, { useState } from 'react';
import classNames from 'classnames';

import { confirmNoticeboardFunding } from '../util/api';
import { fundEscrow } from '../utils/web3-escrow';
import { isUniversalTradeEnabled, lockTradeFunds } from '../utils/web3-universal-trade';

import { PrimaryButton } from './Button/Button';
import IconSpinner from './IconSpinner/IconSpinner';
import Web3TransactionFeedback from './Web3TransactionFeedback/Web3TransactionFeedback';

import css from './FundGigButton.module.css';

export interface FundGigButtonProps {
  noticeId: string;
  escrowAmount: string | number;
  className?: string;
  onFunded?: (transactionHash: string) => void;
}

const FundGigButton: React.FC<FundGigButtonProps> = ({
  noticeId,
  escrowAmount,
  className = null,
  onFunded,
}) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successHash, setSuccessHash] = useState<string | null>(null);
  const [feedbackPhase, setFeedbackPhase] = useState<'processing' | 'success' | null>(null);

  const amountLabel =
    typeof escrowAmount === 'number'
      ? escrowAmount.toString()
      : escrowAmount.trim();

  const handleClick = async () => {
    setBusy(true);
    setError(null);
    setFeedbackPhase('processing');

    try {
      let receipt;
      if (isUniversalTradeEnabled()) {
        const devPin = process.env.REACT_APP_DEV_HANDSHAKE_PIN || '123456';
        receipt = await lockTradeFunds({
          noticeId,
          baseAmountEth: amountLabel,
          bufferAmountEth: '0',
          clearTextPin: devPin,
        });
      } else {
        receipt = await fundEscrow(noticeId, amountLabel);
      }
      const transactionHash = receipt.transactionHash;

      await confirmNoticeboardFunding({
        noticeId,
        transactionHash,
      });

      setSuccessHash(transactionHash);
      setFeedbackPhase('success');
      window.setTimeout(() => setFeedbackPhase(null), 2800);
      onFunded?.(transactionHash);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Escrow funding failed';
      setError(message);
      setFeedbackPhase(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {feedbackPhase ? (
        <Web3TransactionFeedback
          phase={feedbackPhase}
          title={
            feedbackPhase === 'success'
              ? 'Escrow funded successfully'
              : 'Waiting for wallet approval'
          }
          subtitle={
            feedbackPhase === 'success'
              ? 'Your gig is live on the noticeboard.'
              : 'Approve the escrow deposit in your wallet, then wait for mining to finish.'
          }
        />
      ) : null}

      <div className={classNames(css.root, className)} data-testid="fund-gig-button">
        <PrimaryButton
          type="button"
          className={css.fundButton}
          onClick={handleClick}
          disabled={busy || Boolean(successHash)}
          data-testid="fund-gig-button-submit"
        >
          {busy ? (
            <>
              <IconSpinner rootClassName={css.spinner} />
              Funding escrow…
            </>
          ) : successHash ? (
            'Gig live on noticeboard'
          ) : (
            `Fund escrow (${amountLabel} ETH)`
          )}
        </PrimaryButton>
        {error ? (
          <p className={css.error} data-testid="fund-gig-button-error">
            {error}
          </p>
        ) : null}
        {successHash ? (
          <p className={css.success} data-testid="fund-gig-button-success">
            Tx: {successHash.slice(0, 10)}…
          </p>
        ) : null}
      </div>
    </>
  );
};

export default FundGigButton;
